import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Chateau {
  id: string;
  nom: string;
  taille: string;
  capacite: string;
  prix: string;
  description: string;
  caracteristiques: string[];
  age_recommande: string;
  is_visible: boolean;
  display_order: number;
  quantite: number;
  image_position: number;
  created_at: string;
  updated_at: string;
}

export const useChateaux = () => {
  const [chateaux, setChateaux] = useState<Chateau[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchChateaux = async (includeHidden = false) => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('chateaux')
        .select('*')
        .order('display_order', { ascending: true });

      if (!includeHidden) {
        query = query.eq('is_visible', true);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      setChateaux(data || []);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des châteaux:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      toast.error('Erreur lors du chargement des châteaux');
    } finally {
      setLoading(false);
    }
  };

  const createChateau = async (chateauData: Omit<Chateau, 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('chateaux')
        .insert([chateauData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      setChateaux(prev => [...prev, data]);
      toast.success('Château ajouté avec succès');
      return data;
    } catch (err) {
      console.error('Erreur lors de la création du château:', err);
      toast.error('Erreur lors de l\'ajout du château');
      throw err;
    }
  };

  const updateChateau = async (id: string, updates: Partial<Chateau>) => {
    try {
      const { data, error } = await supabase
        .from('chateaux')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      setChateaux(prev => prev.map(chateau => 
        chateau.id === id ? data : chateau
      ));
      toast.success('Château mis à jour avec succès');
      return data;
    } catch (err) {
      console.error('Erreur lors de la mise à jour du château:', err);
      toast.error('Erreur lors de la mise à jour du château');
      throw err;
    }
  };

  const deleteChateau = async (id: string) => {
    try {
      const { error } = await supabase
        .from('chateaux')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      setChateaux(prev => prev.filter(chateau => chateau.id !== id));
      toast.success('Château supprimé avec succès');
    } catch (err) {
      console.error('Erreur lors de la suppression du château:', err);
      toast.error('Erreur lors de la suppression du château');
      throw err;
    }
  };

  const toggleVisibility = async (id: string, isVisible: boolean) => {
    try {
      const { data, error } = await supabase
        .from('chateaux')
        .update({ is_visible: isVisible })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      setChateaux(prev => prev.map(chateau => 
        chateau.id === id ? data : chateau
      ));
      toast.success(`Château ${isVisible ? 'rendu visible' : 'masqué'} avec succès`);
      return data;
    } catch (err) {
      console.error('Erreur lors du changement de visibilité:', err);
      toast.error('Erreur lors du changement de visibilité');
      throw err;
    }
  };

  useEffect(() => {
    fetchChateaux();
  }, []);

  return {
    chateaux,
    loading,
    error,
    fetchChateaux,
    createChateau,
    updateChateau,
    deleteChateau,
    toggleVisibility,
    refetch: fetchChateaux
  };
};